// DEM-45 — assistant-ui Thread for the Engineering PI agent panel.
// Ported from the Erez/template dashboard (MJOS-147). Branding, the empty
// state, and the markdown text renderer are the Engineering-specific changes.
//
// Headless assistant-ui primitives + Tailwind. Renders the message viewport
// (user + assistant bubbles, streaming text), a tool-call chip, and a bottom
// composer (textarea + send button). Markdown text rendering via react-markdown
// so the model's **bold** / lists / inline code show as styled text, not raw
// asterisks (LLMs lean on markdown for structure — fight nature and you lose
// the visual hierarchy of "one item off: PT-203 reads 0 Bar").
import { type FC, useState } from "react";
import ReactMarkdown from "react-markdown";
import {
	ThreadPrimitive,
	MessagePrimitive,
	ComposerPrimitive,
	type ToolCallMessagePartProps,
	type TextMessagePartComponent,
} from "@assistant-ui/react";
import {
	AlertTriangle,
	Check,
	ChevronRight,
	Loader2,
	SendHorizonal,
	Square,
	Wrench,
} from "lucide-react";
import { cn } from "@/lib/utils";

// Component overrides keep the bubble compact: paragraphs tight, lists tighter,
// inline code in a subtle pill, code blocks scrollable. No headings styling —
// the agent shouldn't be writing h1/h2/h3 inside a chat bubble, and if it does
// they render as bold paragraphs (good enough, no visual ballooning).
const markdownComponents = {
	p: ({ children }: { children?: React.ReactNode }) => (
		<p className="mb-1.5 last:mb-0 leading-snug">{children}</p>
	),
	ul: ({ children }: { children?: React.ReactNode }) => (
		<ul className="mb-1.5 list-disc space-y-0.5 pl-4 last:mb-0">{children}</ul>
	),
	ol: ({ children }: { children?: React.ReactNode }) => (
		<ol className="mb-1.5 list-decimal space-y-0.5 pl-4 last:mb-0">{children}</ol>
	),
	li: ({ children }: { children?: React.ReactNode }) => (
		<li className="leading-snug">{children}</li>
	),
	strong: ({ children }: { children?: React.ReactNode }) => (
		<strong className="font-semibold">{children}</strong>
	),
	em: ({ children }: { children?: React.ReactNode }) => <em className="italic">{children}</em>,
	a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
		<a
			href={href}
			target="_blank"
			rel="noreferrer noopener"
			className="underline underline-offset-2 hover:opacity-80"
		>
			{children}
		</a>
	),
	code: ({ children, className }: { children?: React.ReactNode; className?: string }) => {
		const isBlock = typeof className === "string" && className.startsWith("language-");
		return isBlock ? (
			<code className={cn("font-mono text-[12px]", className)}>{children}</code>
		) : (
			<code className="rounded bg-foreground/10 px-1 py-px font-mono text-[12px]">
				{children}
			</code>
		);
	},
	pre: ({ children }: { children?: React.ReactNode }) => (
		<pre className="mb-1.5 max-h-60 overflow-auto rounded-md bg-foreground/10 p-2 text-[12px] leading-snug last:mb-0">
			{children}
		</pre>
	),
	h1: ({ children }: { children?: React.ReactNode }) => (
		<p className="mb-1.5 font-semibold last:mb-0">{children}</p>
	),
	h2: ({ children }: { children?: React.ReactNode }) => (
		<p className="mb-1.5 font-semibold last:mb-0">{children}</p>
	),
	h3: ({ children }: { children?: React.ReactNode }) => (
		<p className="mb-1.5 font-semibold last:mb-0">{children}</p>
	),
};

const TextPart: TextMessagePartComponent = ({ text }) => (
	<div className="break-words">
		<ReactMarkdown components={markdownComponents}>{text}</ReactMarkdown>
	</div>
);

function humanize(name: string): string {
	return name
		.replace(/[_-]+/g, " ")
		.replace(/([a-z])([A-Z])/g, "$1 $2")
		.replace(/\b\w/g, (c) => c.toUpperCase())
		.trim();
}

// For the `zeus` tool we surface the chosen method (it lives in the args) so
// the line reads "Zeus · GetLastKnownValues" rather than a bare "Zeus".
function friendlyLabel(toolName: string, argsText?: string): string {
	if (toolName === "zeus" && argsText) {
		try {
			const a = JSON.parse(argsText);
			if (a?.method) return `Zeus · ${a.method}`;
		} catch {
			/* args may still be streaming — fall through */
		}
		return "Zeus";
	}
	return humanize(toolName);
}

const ToolFallback: FC<ToolCallMessagePartProps> = ({
	toolName,
	argsText,
	result,
	isError,
	status,
}) => {
	const [open, setOpen] = useState(false);

	const errored = isError || status?.type === "incomplete";
	const done = !errored && result != null;
	const running = !errored && !done;

	const StatusIcon = errored ? AlertTriangle : running ? Loader2 : done ? Check : Wrench;
	const statusClass = errored
		? "text-destructive"
		: running
			? "animate-spin text-muted-foreground"
			: done
				? "text-emerald-600"
				: "text-muted-foreground";

	const resultText =
		result == null
			? ""
			: typeof result === "string"
				? result
				: JSON.stringify(result, null, 2);

	return (
		<div className="my-1 rounded-md border bg-muted/40 text-xs text-muted-foreground">
			<button
				type="button"
				onClick={() => setOpen((o) => !o)}
				className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left"
			>
				<StatusIcon className={cn("h-3.5 w-3.5 shrink-0", statusClass)} />
				<span className="min-w-0 flex-1 truncate font-medium text-foreground/80">
					{friendlyLabel(toolName, argsText)}
				</span>
				<ChevronRight
					className={cn("h-3 w-3 shrink-0 transition-transform", open && "rotate-90")}
				/>
			</button>
			{open ? (
				<div className="border-t border-border/60 px-2.5 py-1.5">
					{argsText ? (
						<pre className="max-h-32 overflow-auto whitespace-pre-wrap break-words text-[11px] leading-snug opacity-80">
							{argsText}
						</pre>
					) : null}
					{resultText ? (
						<pre
							className={cn(
								"mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words text-[11px] leading-snug",
								errored ? "text-destructive" : "opacity-90",
							)}
						>
							{resultText}
						</pre>
					) : null}
				</div>
			) : null}
		</div>
	);
};

const messageComponents = {
	Text: TextPart,
	tools: { Fallback: ToolFallback },
} as const;

const UserMessage: FC = () => (
	<MessagePrimitive.Root className="mb-3 flex justify-end">
		<div className="max-w-[85%] rounded-2xl rounded-br-sm bg-primary px-3.5 py-2 text-sm text-primary-foreground">
			<MessagePrimitive.Content components={messageComponents} />
		</div>
	</MessagePrimitive.Root>
);

const AssistantMessage: FC = () => (
	<MessagePrimitive.Root className="mb-3 flex justify-start">
		<div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-secondary px-3.5 py-2 text-sm text-secondary-foreground">
			<MessagePrimitive.Content components={messageComponents} />
		</div>
	</MessagePrimitive.Root>
);

const threadComponents = { UserMessage, AssistantMessage } as const;

export function Thread() {
	return (
		<ThreadPrimitive.Root className="flex h-full flex-col bg-background">
			<ThreadPrimitive.Viewport className="flex-1 overflow-y-auto px-3 py-4">
				<ThreadPrimitive.Empty>
					<div className="flex h-full min-h-[120px] items-center justify-center px-6 text-center text-sm text-muted-foreground">
						Briefing your station…
					</div>
				</ThreadPrimitive.Empty>
				<ThreadPrimitive.Messages components={threadComponents} />
			</ThreadPrimitive.Viewport>

			<Composer />
		</ThreadPrimitive.Root>
	);
}

function Composer() {
	return (
		<ComposerPrimitive.Root className="flex items-end gap-2 border-t bg-background p-2.5">
			<ComposerPrimitive.Input
				rows={1}
				autoFocus
				placeholder="Ask about your station…"
				className={cn(
					"max-h-40 min-h-[40px] flex-1 resize-none rounded-lg border bg-background px-3 py-2 text-sm",
					"placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
				)}
			/>
			<ThreadPrimitive.If running={false}>
				<ComposerPrimitive.Send
					className={cn(
						"flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground",
						"transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40",
					)}
					aria-label="Send message"
				>
					<SendHorizonal className="h-[18px] w-[18px]" />
				</ComposerPrimitive.Send>
			</ThreadPrimitive.If>
			<ThreadPrimitive.If running>
				<ComposerPrimitive.Cancel
					className={cn(
						"flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-secondary-foreground",
						"transition-colors hover:bg-secondary/80",
					)}
					aria-label="Stop generating"
				>
					<Square className="h-[16px] w-[16px]" />
				</ComposerPrimitive.Cancel>
			</ThreadPrimitive.If>
		</ComposerPrimitive.Root>
	);
}
